import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserGroupIcon, ClockIcon } from '@heroicons/react/24/solid';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import type { TvRound, TvPairingGroup, TvTournament } from '../hooks/useTvData';

interface Props {
  tournament: TvTournament | null;
  rounds: TvRound[];
  orgLogoUrl?: string | null;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const ALL_CODES = Object.keys(Flags) as (keyof typeof Flags)[];

function resolveCountryCode(country: string): string | null {
  const trimmed = country.trim();
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && hasFlag(upper)) return upper;
  for (const code of ALL_CODES) {
    try {
      if (regionNames.of(code)?.toLowerCase() === trimmed.toLowerCase()) return code;
    } catch { /* ignore */ }
  }
  return null;
}

function CountryFlag({ country }: { country: string }) {
  const code = resolveCountryCode(country);
  if (!code) return null;
  const FlagComponent = Flags[code as keyof typeof Flags];
  return <FlagComponent title={country.trim()} className="w-4 h-3 rounded-sm flex-shrink-0" />;
}

function formatTeeTime(t: string | null): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

const GROUP_COLS = [
  'grid-cols-1',
  'grid-cols-1',
  'grid-cols-2',
  'grid-cols-2',
  'grid-cols-3',
  'grid-cols-3',
  'grid-cols-3',
  'grid-cols-4',
  'grid-cols-4',
];

function getGridCols(count: number): string {
  if (count <= 0) return 'grid-cols-1';
  return GROUP_COLS[Math.min(count, GROUP_COLS.length - 1)] ?? 'grid-cols-4';
}

const PAGE_SIZE_GROUPS = 8;
const PAGE_INTERVAL = 9000;

function GroupCard({ group }: { group: TvPairingGroup }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl lg:rounded-2xl overflow-hidden flex flex-col h-full">
      <div className="flex items-center justify-between px-3 lg:px-4 py-2 lg:py-2.5 bg-gradient-to-r from-golf-green/40 to-golf-green/10 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-xs lg:text-sm tracking-wide">Group {group.group_number}</span>
          <span className={`text-[9px] lg:text-[10px] font-bold px-1.5 py-0.5 rounded ${
            (group.starting_hole ?? 1) === 10
              ? 'bg-blue-500/30 text-blue-200'
              : 'bg-emerald-500/30 text-emerald-200'
          }`}>
            H{group.starting_hole ?? 1}
          </span>
        </div>
        {group.tee_time && (
          <span className="flex items-center gap-1 lg:gap-1.5 text-golf-orange font-semibold text-[10px] lg:text-xs">
            <ClockIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
            {formatTeeTime(group.tee_time)}
          </span>
        )}
      </div>
      <div className="flex flex-col divide-y divide-white/5 flex-1">
        {group.pairings.map((p) => {
          const player = p.player;
          if (!player) return null;
          return (
            <div key={p.id} className="flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-2.5 flex-1">
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-xs lg:text-sm truncate">
                  {player.first_name} {player.last_name}
                </p>
                <div className="flex items-center gap-1.5 lg:gap-2 mt-0.5">
                  {player.country && <CountryFlag country={player.country} />}
                  {player.category && (
                    <span className="text-[8px] lg:text-[9px] font-bold uppercase tracking-wider px-1 lg:px-1.5 py-px rounded-full bg-golf-green/50 text-green-300">
                      {player.category}
                    </span>
                  )}
                  {player.resident_club && (
                    <span className="text-white/40 text-[9px] lg:text-[10px] truncate hidden lg:inline">{player.resident_club}</span>
                  )}
                </div>
              </div>
              {player.handicap != null && (
                <span className="text-white/50 text-[10px] lg:text-xs font-medium flex-shrink-0">HCP {player.handicap}</span>
              )}
            </div>
          );
        })}
        {group.pairings.length === 0 && (
          <div className="px-3 lg:px-4 py-3">
            <p className="text-white/30 text-xs italic">No players assigned</p>
          </div>
        )}
      </div>
    </div>
  );
}

function isGroupPastTeeOff(group: TvPairingGroup, roundDate: string | null): boolean {
  if (!roundDate || !group.tee_time) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (roundDate !== today) return false;
  const [h, m] = group.tee_time.split(':').map(Number);
  const teeOffMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m).getTime();
  return now.getTime() > teeOffMs + 10 * 60 * 1000;
}

export default function TvPairingsSlide({ tournament, rounds, orgLogoUrl }: Props) {
  const [selectedRoundIdx, setSelectedRoundIdx] = useState(0);
  const [page, setPage] = useState(0);
  const [, setTick] = useState(0);

  // Re-render every minute to hide groups that have teed off
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  const round = rounds[selectedRoundIdx] ?? null;
  const groups = [...(round?.groups ?? [])]
    .filter(g => !isGroupPastTeeOff(g, round?.date ?? null))
    .sort((a, b) => {
      const timeA = a.tee_time ?? '';
      const timeB = b.tee_time ?? '';
      if (timeA !== timeB) return timeA.localeCompare(timeB);
      const holeA = a.starting_hole ?? 1;
      const holeB = b.starting_hole ?? 1;
      if (holeA !== holeB) return holeA - holeB;
      return a.group_number - b.group_number;
    });
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE_GROUPS));

  useEffect(() => {
    setPage(0);
  }, [selectedRoundIdx]);

  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => {
      setPage((p) => (p + 1) % totalPages);
    }, PAGE_INTERVAL);
    return () => clearInterval(timer);
  }, [totalPages]);

  const visibleGroups = groups.slice(page * PAGE_SIZE_GROUPS, (page + 1) * PAGE_SIZE_GROUPS);
  const gridCols = getGridCols(visibleGroups.length);

  return (
    <div className="flex flex-col h-full bg-golf-gray text-white overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-6 lg:px-10 xl:px-16 py-3 sm:py-4 lg:py-6 bg-gradient-to-r from-golf-green to-[#2d7a44] border-b border-white/10 gap-3 flex-shrink-0">
        <div className="flex items-center gap-3 sm:gap-6 min-w-0">
          <div className="h-10 sm:h-14 lg:h-20 w-auto max-w-[80px] sm:max-w-[130px] lg:max-w-[180px] flex items-center justify-center flex-shrink-0">
            {orgLogoUrl ? (
              <img src={orgLogoUrl} alt="Tournament logo" className="h-full w-auto object-contain drop-shadow-lg" />
            ) : (
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center bg-white/10 shadow-lg">
                <UserGroupIcon className="w-6 h-6 sm:w-9 sm:h-9 text-white" />
              </div>
            )}
          </div>
          <div className="w-px h-8 sm:h-14 bg-white/20 hidden sm:block" />
          <div className="min-w-0">
            <p className="text-white/60 text-xs sm:text-base lg:text-lg font-medium uppercase tracking-widest">
              Player Pairings
            </p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold font-display truncate">
              {tournament?.name ?? 'Tournament'}
            </h2>
          </div>
        </div>

        {rounds.length > 1 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {rounds.map((r, i) => (
              <button
                key={r.id}
                onClick={() => setSelectedRoundIdx(i)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                  i === selectedRoundIdx
                    ? 'bg-golf-orange text-white shadow'
                    : 'bg-black/30 text-white/50 border border-white/10 hover:text-white hover:bg-white/10'
                }`}
              >
                {r.name ?? `Round ${r.round_number}`}
              </button>
            ))}
          </div>
        )}
      </div>

      {round && (
        <div className="px-3 sm:px-6 lg:px-10 xl:px-16 py-2 flex items-center justify-between border-b border-white/5 flex-shrink-0">
          <p className="text-white/40 text-xs sm:text-sm font-semibold uppercase tracking-widest">
            {round.name ?? `Round ${round.round_number}`}
            {round.date && (
              <span className="ml-3 font-normal normal-case tracking-normal">
                {new Date(round.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </span>
            )}
          </p>
          <p className="text-white/30 text-xs">
            {groups.length} {groups.length === 1 ? 'group' : 'groups'}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-hidden px-3 sm:px-6 lg:px-10 xl:px-16 py-3 sm:py-4 lg:py-6 flex flex-col gap-3 lg:gap-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedRoundIdx}-${page}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45 }}
            className={`grid ${gridCols} gap-2 sm:gap-3 lg:gap-4 flex-1 auto-rows-fr`}
          >
            {visibleGroups.map((g) => (
              <GroupCard key={g.group_number} group={g} />
            ))}
            {visibleGroups.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center h-40 gap-3">
                <UserGroupIcon className="w-12 h-12 text-white/20" />
                <p className="text-white/30 text-lg">No pairings available</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 flex-shrink-0 pb-1">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === page
                    ? 'w-8 h-2 bg-golf-orange shadow-sm shadow-golf-orange/50'
                    : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
